import React, { useMemo, useCallback } from 'react';
import {
	DataGrid,
	DataGridHeader,
	DataGridHeaderCell,
	DataGridBody,
	DataGridRow,
	DataGridCell,
	TableColumnDefinition,
	createTableColumn,
	Button,
	makeStyles,
	shorthands,
	tokens
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

const useStyles = makeStyles({
	root: { width: '100%', overflowX: 'auto' },
	negative: { color: tokens.colorStatusDangerForeground1, fontWeight: 600 },
	positive: { color: tokens.colorStatusSuccessForeground1, fontWeight: 600 },
	warn: { backgroundColor: tokens.colorStatusDangerBackground1 },
	toolbar: { display:'flex', justifyContent:'flex-end', gap: '8px', marginBottom: '8px' },
	table: { ...shorthands.border('1px','solid', tokens.colorNeutralStroke1), borderRadius: '4px' }
});

export interface ProyectionDataGridProps {
	monthlyRevenue: number[];                 // ingresos mensuales (null/0 sin ingreso)
	providerCosts?: number[];                 // costos proveedores
	medicalTeamCosts?: number[];              // costos equipo médico
	monthlyCosts?: number[];                  // costos totales (si no se pasa se deriva)
	providerCostShare?: number;               // si se deriva el split
	providerCostLabel?: string;               // etiqueta costos proveedores
	medicalTeamCostLabel?: string;            // etiqueta costos equipo médico
	revenueLabel?: string;                    // etiqueta ingresos
	netLabel?: string;                        // etiqueta neto
	cumulativeLabel?: string;                 // etiqueta acumulado
	marginLabel?: string;                     // etiqueta margen
	currencySymbol?: string;
	minMarginThreshold?: number;              // % mínimo
	cumulative?: number[];                    // saldo acumulado (si no se pasa se calcula como ingresos - costos)
	cumulativeMinProfit?: number[];           // acumulado margen mínimo
	marginPerc?: (number|null)[];             // margen % mensual
	showCumulativeMinProfitLine?: boolean;    // mostrar columna acumulado mínimo
	showMargin?: boolean;                     // mostrar margen
	showNet?: boolean;                        // mostrar neto
	showCostsSplit?: boolean;                 // mostrar columnas separadas de costos
	onExportCsv?: () => void;                 // callback externo opcional
	enableExport?: boolean;                   // muestra botón exportar
}

interface RowModel {
	id: number;
	month: string;
	revenue: number;
	providerCost: number;
	medicalTeamCost: number;
	totalCost: number;
	net: number;
	cumulative: number;
	marginPerc: number | null;
	cumulativeMinProfit: number | null;
}

const formatMoney = (v: number | null | undefined, symbol: string) => {
	if (v == null || isNaN(v as number)) return '-';
	return `${symbol}${(v as number).toLocaleString(undefined,{ maximumFractionDigits:0 })}`;
};

const ProyectionDataGridComponent: React.FC<ProyectionDataGridProps> = ({
	monthlyRevenue,
	providerCosts,
	medicalTeamCosts,
	monthlyCosts,
	providerCostShare = 0.6,
	providerCostLabel,
	medicalTeamCostLabel,
	revenueLabel,
	netLabel,
	cumulativeLabel,
	marginLabel,
	currencySymbol = '$',
	minMarginThreshold = 35,
	cumulative,
	cumulativeMinProfit,
	marginPerc,
	showCumulativeMinProfitLine = true,
	showMargin = true,
	showNet = true,
	showCostsSplit = true,
	onExportCsv,
	enableExport = true
}) => {
	const { t } = useTranslation();
	const classes = useStyles();

	// Translated labels
	const providerCostLabelText = providerCostLabel || t('providerCosts');
	const medicalTeamCostLabelText = medicalTeamCostLabel || t('medicalTeamCosts');
	const revenueLabelText = revenueLabel || t('income');
	const netLabelText = netLabel || t('net');
	const cumulativeLabelText = cumulativeLabel || t('cumulative');
	const marginLabelText = marginLabel || t('marginPercent');
	const monthNames = MONTH_KEYS.map(key => t(key));

	// Derivar costos totales si no vienen
	const totalCosts = useMemo(() => {
		if (monthlyCosts && monthlyCosts.length === 12) return monthlyCosts;
		if (providerCosts && medicalTeamCosts && providerCosts.length === 12 && medicalTeamCosts.length === 12) {
			return providerCosts.map((p,i)=> (p || 0) + (medicalTeamCosts[i] || 0));
		}
		// derivar a partir de revenue y porcentaje aproximado  (asumimos 62%)
		return monthlyRevenue.map(r => Math.round(r * 0.62));
	}, [monthlyCosts, providerCosts, medicalTeamCosts, monthlyRevenue]);

	// Derivar split si falta
	const providerCostsDerived = useMemo(() => {
		if (providerCosts && providerCosts.length === 12) return providerCosts;
		return totalCosts.map(c => Math.round(c * providerCostShare));
	}, [providerCosts, totalCosts, providerCostShare]);

	const medicalTeamCostsDerived = useMemo(() => {
		if (medicalTeamCosts && medicalTeamCosts.length === 12) return medicalTeamCosts;
		return totalCosts.map((c,i)=> Math.max(0, c - providerCostsDerived[i]));
	}, [medicalTeamCosts, totalCosts, providerCostsDerived]);

	// Neto y acumulado si no se pasa
	const netMonthly = useMemo(() => monthlyRevenue.map((r,i)=> r - totalCosts[i]), [monthlyRevenue, totalCosts]);
	const cumulativeSeries = useMemo(() => {
		if (cumulative && cumulative.length === 12) return cumulative;
		let acc = 0; return netMonthly.map(v => acc += v);
	}, [cumulative, netMonthly]);

	// Margen %
	const marginPercSeries = useMemo(() => {
		if (marginPerc && marginPerc.length === 12) return marginPerc;
		return monthlyRevenue.map((r,i)=> r ? +(((r - totalCosts[i]) / r) * 100).toFixed(2) : null);
	}, [marginPerc, monthlyRevenue, totalCosts]);

	// Acumulado de rentabilidad mínima si no viene
	const cumulativeMinProfitSeries = useMemo(() => {
		if (!showCumulativeMinProfitLine) return Array(12).fill(null);
		if (cumulativeMinProfit && cumulativeMinProfit.length === 12) return cumulativeMinProfit;
		const arr: number[] = []; let acc = 0;
		for (let i=0;i<12;i++) { const minP = (monthlyRevenue[i]||0) * (minMarginThreshold/100); acc += minP; arr.push(Math.round(acc)); }
		return arr;
	}, [cumulativeMinProfit, showCumulativeMinProfitLine, monthlyRevenue, minMarginThreshold]);

	const rows: RowModel[] = useMemo(() => {
		return monthNames.map((m,i) => ({
			id: i,
			month: m,
			revenue: monthlyRevenue[i] || 0,
			providerCost: providerCostsDerived[i] || 0,
			medicalTeamCost: medicalTeamCostsDerived[i] || 0,
			totalCost: totalCosts[i] || 0,
			net: netMonthly[i] || 0,
			cumulative: cumulativeSeries[i] || 0,
			marginPerc: marginPercSeries[i],
			cumulativeMinProfit: cumulativeMinProfitSeries[i] ?? null
		}));
	}, [monthNames, monthlyRevenue, providerCostsDerived, medicalTeamCostsDerived, totalCosts, netMonthly, cumulativeSeries, marginPercSeries, cumulativeMinProfitSeries]);

	const exportCsv = useCallback(() => {
		const headers = [
			t('month'),
			revenueLabelText,
			providerCostLabelText,
			medicalTeamCostLabelText,
			t('totalCost'),
			netLabelText,
			cumulativeLabelText,
			marginLabelText,
			t('cumulativeMinProfit')
		];
		const lines = rows.map(r => [
			r.month,
			r.revenue,
			r.providerCost,
			r.medicalTeamCost,
			r.totalCost,
			r.net,
			r.cumulative,
			r.marginPerc ?? '',
			r.cumulativeMinProfit ?? ''
		].join(','));
		const csv = [headers.join(','), ...lines].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'proyeccion.csv';
		a.click();
		URL.revokeObjectURL(url);
		onExportCsv && onExportCsv();
	}, [rows, onExportCsv, t, revenueLabelText, providerCostLabelText, medicalTeamCostLabelText, netLabelText, cumulativeLabelText, marginLabelText]);

	// Column definitions
	const columns = useMemo<TableColumnDefinition<RowModel>[]>(() => {
		const cols: TableColumnDefinition<RowModel>[] = [];
		cols.push(createTableColumn<RowModel>({
			columnId: 'month',
			compare: (a,b)=> a.id - b.id,
			renderHeaderCell: () => t('month'),
			renderCell: (item) => item.month
		}));
		cols.push(createTableColumn<RowModel>({
			columnId: 'revenue',
			compare: (a,b)=> a.revenue - b.revenue,
			renderHeaderCell: () => revenueLabelText,
			renderCell: (item) => formatMoney(item.revenue, currencySymbol)
		}));
		if (showCostsSplit) {
			cols.push(createTableColumn<RowModel>({
				columnId: 'providerCost',
				compare: (a,b)=> a.providerCost - b.providerCost,
				renderHeaderCell: () => providerCostLabelText,
				renderCell: (item) => formatMoney(item.providerCost, currencySymbol)
			}));
			cols.push(createTableColumn<RowModel>({
				columnId: 'medicalTeamCost',
				compare: (a,b)=> a.medicalTeamCost - b.medicalTeamCost,
				renderHeaderCell: () => medicalTeamCostLabelText,
				renderCell: (item) => formatMoney(item.medicalTeamCost, currencySymbol)
			}));
		}
		cols.push(createTableColumn<RowModel>({
			columnId: 'totalCost',
			compare: (a,b)=> a.totalCost - b.totalCost,
			renderHeaderCell: () => t('totalCost'),
			renderCell: (item) => formatMoney(item.totalCost, currencySymbol)
		}));
		if (showNet) {
			cols.push(createTableColumn<RowModel>({
				columnId: 'net',
				compare: (a,b)=> a.net - b.net,
				renderHeaderCell: () => netLabelText,
				renderCell: (item) => {
					const cls = item.net < 0 ? classes.negative : classes.positive;
					return <span className={cls}>{formatMoney(item.net, currencySymbol)}</span>;
				}
			}));
			cols.push(createTableColumn<RowModel>({
				columnId: 'cumulative',
				compare: (a,b)=> a.cumulative - b.cumulative,
				renderHeaderCell: () => cumulativeLabelText,
				renderCell: (item) => formatMoney(item.cumulative, currencySymbol)
			}));
		}
		if (showMargin) {
			cols.push(createTableColumn<RowModel>({
				columnId: 'marginPerc',
				compare: (a,b)=> (a.marginPerc||0) - (b.marginPerc||0),
				renderHeaderCell: () => marginLabelText,
				renderCell: (item) => {
					if (item.marginPerc == null) return '-';
						const below = item.marginPerc < minMarginThreshold;
						return <span className={below ? classes.negative : classes.positive}>{item.marginPerc.toFixed(2)}%</span>;
				}
			}));
		}
		if (showCumulativeMinProfitLine) {
			cols.push(createTableColumn<RowModel>({
				columnId: 'cumulativeMinProfit',
				compare: (a,b)=> (a.cumulativeMinProfit||0) - (b.cumulativeMinProfit||0),
				renderHeaderCell: () => t('cumulativeMinProfit'),
				renderCell: (item) => item.cumulativeMinProfit == null ? '-' : formatMoney(item.cumulativeMinProfit, currencySymbol)
			}));
		}
		return cols;
	}, [classes, currencySymbol, minMarginThreshold, showCostsSplit, showNet, showMargin, showCumulativeMinProfitLine, t, revenueLabelText, providerCostLabelText, medicalTeamCostLabelText, netLabelText, cumulativeLabelText, marginLabelText]);

	return (
		<div className={classes.root}>
			{enableExport && (
				<div className={classes.toolbar}>
					<Button appearance="primary" size="small" onClick={exportCsv}>{t('exportCsv')}</Button>
				</div>
			)}
			<DataGrid
				items={rows}
				columns={columns}
				getRowId={item => item.id}
				sortable
				className={classes.table}
				focusMode="cell"
			>
				<DataGridHeader>
					<DataGridRow>
						{({ renderHeaderCell }) => (
							<DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
						)}
					</DataGridRow>
				</DataGridHeader>
				<DataGridBody<RowModel>>
					{({ item, rowId }) => (
						<DataGridRow key={rowId}>
							{({ renderCell, columnId }) => (
								<DataGridCell data-column={columnId}>{renderCell(item)}</DataGridCell>
							)}
						</DataGridRow>
					)}
				</DataGridBody>
			</DataGrid>
		</div>
	);
};

export default ProyectionDataGridComponent;